// frontend/src/ui/mobileShell.js
import { virtualInput } from './virtualInput.js';
import { computeStickVector } from './joystickMath.js';
import { isTouchDevice } from './isTouchDevice.js';
import { iconHtml } from './iconHtml.mjs';
import { shellVisibility } from './shellVisibility.mjs';
import { shieldFromPhaser } from './uiShield.js';

const RADIUS = 60;
const DEADZONE = 12;

// `isLocked`: true while any running scene holds an input lock (a modal is open).
export function mountMobileShell({ isLocked = () => false } = {}) {
  if (!isTouchDevice()) return;
  document.body.classList.add('is-touch');

  // --- Joystick (bottom-left) ---
  const base = document.createElement('div');
  base.style.cssText = [
    'position:fixed', 'left:24px', 'bottom:24px', 'z-index:1500',
    `width:${RADIUS * 2}px`, `height:${RADIUS * 2}px`, 'border-radius:50%',
    'background:#ffffff22', 'border:3px solid #ffffff55',
    'touch-action:none', 'user-select:none',
  ].join(';');
  const knob = document.createElement('div');
  knob.style.cssText = [
    'position:absolute', 'left:50%', 'top:50%', 'transform:translate(-50%,-50%)',
    'width:60px', 'height:60px', 'border-radius:50%',
    'background:#ffffffcc', 'pointer-events:none',
  ].join(';');
  base.appendChild(knob);

  let activeId = null;
  const center = () => {
    const r = base.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };
  const onMove = (e) => {
    if (activeId !== null && e.pointerId !== activeId) return;
    const c = center();
    const dx = e.clientX - c.x;
    const dy = e.clientY - c.y;
    const v = computeStickVector(dx, dy, RADIUS, DEADZONE);
    virtualInput.setVector(v.x, v.y);
    knob.style.transform = `translate(calc(-50% + ${v.x * RADIUS}px), calc(-50% + ${v.y * RADIUS}px))`;
  };
  const release = () => {
    activeId = null;
    virtualInput.clearVector();
    knob.style.transform = 'translate(-50%,-50%)';
  };
  base.addEventListener('pointerdown', (e) => { activeId = e.pointerId; base.setPointerCapture(e.pointerId); onMove(e); });
  base.addEventListener('pointermove', onMove);
  base.addEventListener('pointerup', release);
  base.addEventListener('pointercancel', release);
  base.addEventListener('pointerleave', release);
  document.body.appendChild(base);
  // Phaser listens on window too: an unshielded release of the stick reached it as a tap on
  // whatever lay under the thumb — the dark overlay of the unlock question that walking had
  // just opened, which closed it as "Nie" (a phone could never open the house).
  shieldFromPhaser(base, { keys: false });

  // --- Action + Cancel buttons (bottom-right) ---
  const mkBtn = (bg) => {
    const b = document.createElement('button');
    b.style.cssText = [
      'position:fixed', 'right:10px', 'z-index:1500',
      'min-width:96px', 'height:64px', 'border:none', 'border-radius:16px',
      `background:${bg}`, 'color:#000', 'font:bold 20px system-ui',
      'box-shadow:0 2px 6px #0006', 'touch-action:none', 'display:none',
    ].join(';');
    document.body.appendChild(b);
    shieldFromPhaser(b, { keys: false });
    return b;
  };
  const actionBtn = mkBtn('#ffd166');
  actionBtn.style.bottom = '14px';
  const cancelBtn = mkBtn('#ff7b7b');
  cancelBtn.style.bottom = '88px';
  cancelBtn.innerHTML = iconHtml('{cross} anuluj');

  actionBtn.addEventListener('click', () => virtualInput.invokeAction());
  cancelBtn.addEventListener('click', () => virtualInput.invokeCancel());

  const refresh = () => {
    const a = virtualInput.getAction();
    const show = shellVisibility({ stickWanted: virtualInput.stickWanted(), locked: isLocked(),
      hasAction: !!a, hasCancel: !!virtualInput.getCancel() });
    base.style.display = show.stick ? 'block' : 'none';
    if (!show.stick && activeId !== null) release();
    actionBtn.style.display = show.action ? 'block' : 'none';
    if (a) actionBtn.innerHTML = iconHtml(a.label);   // labels may carry {icon} tokens
    cancelBtn.style.display = show.cancel ? 'block' : 'none';
    document.body.classList.toggle('shell-modal', !show.chat);   // hides the chat chip / palette (index.html)
  };
  setInterval(refresh, 150);   // modals lock input without telling the shell
  virtualInput.subscribe(refresh);
  refresh();
}
